import { useState } from "react";
import { API } from "../../App";

export default function AdminProductsTab({ 
  productsData, 
  adminKey, 
  onRefresh 
}) {
  const [showProductModal, setShowProductModal] = useState(null);
  const [productForm, setProductForm] = useState({
    name: "",
    description: "",
    price: "",
    compare_at_price: "",
    category: "sprays",
    product_type: "physical",
    images: "",
    stock_quantity: 0,
    is_exclusive: false,
    inner_circle_discount_percent: 0,
    digital_download_url: "",
    is_active: true
  });

  const openCreateModal = () => {
    setProductForm({
      name: "", description: "", price: "", compare_at_price: "",
      category: "sprays", product_type: "physical", images: "",
      stock_quantity: 0, is_exclusive: false, inner_circle_discount_percent: 0,
      digital_download_url: "", is_active: true
    });
    setShowProductModal({ mode: "create" });
  };

  const openEditModal = (product) => {
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      compare_at_price: product.compare_at_price?.toString() || "",
      category: product.category,
      product_type: product.product_type,
      images: product.images?.join(", ") || "",
      stock_quantity: product.stock_quantity,
      is_exclusive: product.is_exclusive,
      inner_circle_discount_percent: product.inner_circle_discount_percent,
      digital_download_url: product.digital_download_url || "",
      is_active: product.is_active
    });
    setShowProductModal({ mode: "edit", product_id: product.product_id });
  };

  const saveProduct = async () => {
    const payload = {
      ...productForm,
      price: parseFloat(productForm.price),
      compare_at_price: productForm.compare_at_price ? parseFloat(productForm.compare_at_price) : null,
      images: productForm.images.split(",").map(s => s.trim()).filter(Boolean)
    };
    
    if (showProductModal.mode === "create") {
      await fetch(`${API}/admin/products?admin_key=${adminKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
    } else {
      await fetch(`${API}/admin/products/${showProductModal.product_id}?admin_key=${adminKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
    }
    setShowProductModal(null);
    onRefresh();
  };

  const deleteProduct = async (productId) => {
    if (confirm("Delete this product?")) {
      await fetch(`${API}/admin/products/${productId}?admin_key=${adminKey}`, { method: "DELETE" });
      onRefresh();
    }
  };

  return (
    <div>
      {/* Add Product Button */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-[#2D2A26]">Products</h2>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C] text-sm"
        >
          + Add Product
        </button>
      </div>

      {/* Low Stock Alert */}
      {productsData?.low_stock_count > 0 && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
          <p className="text-red-700 font-medium mb-2">⚠️ Low Stock Alert</p>
          <p className="text-red-600 text-sm">
            {productsData.low_stock_products.map(p => p.name).join(", ")} — running low on inventory
          </p>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {productsData?.products?.map(product => (
          <div key={product.product_id} className="bg-white rounded-xl border border-[#E8E4DE] overflow-hidden">
            <div className="aspect-video bg-[#F5F2EE] relative">
              {product.images?.[0] ? (
                <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#A8A4A0]">No Image</div>
              )}
              {!product.is_active && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white font-medium">Inactive</span>
                </div>
              )}
              {product.is_exclusive && (
                <span className="absolute top-2 left-2 px-2 py-1 bg-[#A87C6D] text-white text-xs rounded">Exclusive</span>
              )}
            </div>
            <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-medium text-[#2D2A26]">{product.name}</h3>
                  <p className="text-sm text-[#6E6A66] capitalize">{product.category}</p>
                </div>
                <span className="text-lg font-medium text-[#A87C6D]">${product.price}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className={product.product_type === "physical" && product.stock_quantity <= 5 ? "text-red-500" : "text-[#6E6A66]"}>
                  {product.product_type === "digital" ? "Digital" : `Stock: ${product.stock_quantity}`}
                </span>
                {product.inner_circle_discount_percent > 0 && (
                  <span className="text-[#7A9E7E]">{product.inner_circle_discount_percent}% off for IC</span>
                )}
              </div>
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => openEditModal(product)}
                  className="flex-1 px-3 py-2 text-sm border border-[#E8E4DE] rounded-lg hover:bg-[#F5F2EE]"
                >
                  Edit
                </button>
                <button
                  onClick={() => deleteProduct(product.product_id)}
                  className="px-3 py-2 text-sm text-red-500 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {(!productsData?.products || productsData.products.length === 0) && (
        <div className="text-center py-12 text-[#6E6A66]">
          No products yet. Add your first product to get started.
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium text-[#2D2A26] mb-4">
              {showProductModal.mode === "create" ? "Add Product" : "Edit Product"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Name</label>
                <input
                  type="text"
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Description</label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.price}
                    onChange={(e) => setProductForm({...productForm, price: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Compare at ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={productForm.compare_at_price}
                    onChange={(e) => setProductForm({...productForm, compare_at_price: e.target.value})}
                    placeholder="Optional"
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Category</label>
                  <select
                    value={productForm.category}
                    onChange={(e) => setProductForm({...productForm, category: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  >
                    <option value="sprays">Aura Sprays</option>
                    <option value="incense">Incense</option>
                    <option value="candles">Candles</option>
                    <option value="journals">Journals</option>
                    <option value="digital">Digital</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Type</label>
                  <select
                    value={productForm.product_type}
                    onChange={(e) => setProductForm({...productForm, product_type: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  >
                    <option value="physical">Physical</option>
                    <option value="digital">Digital</option>
                  </select>
                </div>
              </div>
              {productForm.product_type === "physical" && (
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Stock Quantity</label>
                  <input
                    type="number"
                    value={productForm.stock_quantity}
                    onChange={(e) => setProductForm({...productForm, stock_quantity: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  />
                </div>
              )}
              {productForm.product_type === "digital" && (
                <div>
                  <label className="block text-sm font-medium text-[#6E6A66] mb-1">Download URL</label>
                  <input
                    type="url"
                    value={productForm.digital_download_url}
                    onChange={(e) => setProductForm({...productForm, digital_download_url: e.target.value})}
                    className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                    placeholder="https://..."
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Image URLs (comma separated)</label>
                <input
                  type="text"
                  value={productForm.images}
                  onChange={(e) => setProductForm({...productForm, images: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                  placeholder="https://image1.jpg, https://image2.jpg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6E6A66] mb-1">Inner Circle Discount (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={productForm.inner_circle_discount_percent}
                  onChange={(e) => setProductForm({...productForm, inner_circle_discount_percent: parseInt(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-[#E8E4DE] rounded-lg"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={productForm.is_exclusive}
                    onChange={(e) => setProductForm({...productForm, is_exclusive: e.target.checked})}
                  />
                  <span className="text-sm text-[#6E6A66]">Inner Circle Exclusive</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={productForm.is_active}
                    onChange={(e) => setProductForm({...productForm, is_active: e.target.checked})}
                  />
                  <span className="text-sm text-[#6E6A66]">Active</span>
                </label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowProductModal(null)}
                className="flex-1 px-4 py-2 border border-[#E8E4DE] rounded-lg hover:bg-[#F5F2EE]"
              >
                Cancel
              </button>
              <button
                onClick={saveProduct}
                className="flex-1 px-4 py-2 bg-[#A87C6D] text-white rounded-lg hover:bg-[#96695C]"
              >
                {showProductModal.mode === "create" ? "Create Product" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
